import { z } from 'zod'
import type { SupabaseClient } from '@supabase/supabase-js'
import { phoneSearchPattern } from '@/shared/domain/searchTokens'

const idRowSchema = z.object({ id: z.string() })

/**
 * Soumissions, Contrats et Factures ne portent ni le nom, ni le téléphone, ni
 * le courriel de la personne : ces champs vivent sur `clients` et `leads`. Un
 * `.or()` PostgREST sur une relation embarquée ne filtre pas les lignes du
 * haut; on cherche donc d'abord les personnes qui correspondent (chaque mot
 * doit correspondre à au moins un champ, tous les mots doivent correspondre),
 * puis l'appelant élargit sa recherche à `client_id.in.(...)`.
 *
 * La fonction `search_party_ids_v1` ignore les accents (« gerard » retrouve
 * « Gérard »), ce que `ilike` ne sait pas faire. Tant qu'elle n'est pas
 * déployée sur la base visée, la recherche `ilike` sert de repli.
 *
 * Les mots reçus sont déjà nettoyés des caractères réservés de PostgREST.
 */
async function findIds(
  client: SupabaseClient,
  table: 'clients' | 'leads',
  columns: readonly string[],
  tokens: string[],
): Promise<string[]> {
  if (tokens.length === 0) return []
  const rpc = (await client.rpc('search_party_ids_v1', { p_kind: table, p_tokens: tokens })) as {
    data: unknown
    error: unknown
  }
  if (!rpc.error && Array.isArray(rpc.data)) {
    return rpc.data.filter((id): id is string => typeof id === 'string')
  }
  let query = client.from(table).select('id').is('deleted_at', null).limit(200)
  for (const token of tokens) {
    query = query.or(
      columns
        .map((column) =>
          column.startsWith('telephone')
            ? `${column}.ilike.${phoneSearchPattern(token)}`
            : `${column}.ilike.%${token}%`,
        )
        .join(','),
    )
  }
  const response = (await query) as { data: unknown; error: unknown }
  if (response.error || !Array.isArray(response.data)) return []
  const ids = new Set<string>()
  for (const candidate of response.data) {
    const parsed = idRowSchema.safeParse(candidate)
    if (parsed.success) ids.add(parsed.data.id)
  }
  return [...ids]
}

const clientColumns = [
  'prenom',
  'nom',
  'entreprise',
  'telephone',
  'telephone_mobile',
  'courriel',
  'adresse',
  'ville',
  'code_postal',
] as const

const leadColumns = ['prenom', 'nom', 'telephone', 'courriel', 'adresse', 'ville', 'code_postal'] as const

export function findClientIdsBySearch(client: SupabaseClient, tokens: string[]): Promise<string[]> {
  return findIds(client, 'clients', clientColumns, tokens)
}

export function findLeadIdsBySearch(client: SupabaseClient, tokens: string[]): Promise<string[]> {
  return findIds(client, 'leads', leadColumns, tokens)
}
